// v2.2 US-1.4: one place that formats an event's date, time, label and
// status for every screen (coach/parent schedules and dashboards). Dates are
// wall-clock strings from the API ('YYYY-MM-DD', 'HH:MM') in the program's
// time zone, so they are formatted WITHOUT converting through the browser
// zone (which used to shift dates by a day for some users).

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const parseYmd = (ymd) => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return null;
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  return { y, m, d, date: new Date(Date.UTC(y, m - 1, d)) };
};

// { day: 3, month: 'OCT', year: 2026, full: 'Saturday, October 3, 2026', short: 'Sat, Oct 3' }
export const formatEventDate = (ymd) => {
  const p = parseYmd(ymd);
  if (!p) return { day: '--', month: '', year: '', full: 'Date TBD', short: 'TBD' };
  return {
    day: p.d,
    month: MONTHS[p.m - 1],
    year: p.y,
    full: p.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }),
    short: p.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }),
  };
};

// 'HH:MM' -> 'h:MM AM'; passthrough for free text; 'TBD' when empty.
export const formatEventTime = (t) => {
  if (!t) return 'TBD';
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!m) return t;
  let h = Number(m[1]);
  const min = m[2];
  if (m[3]) return `${h}:${min} ${m[3].toUpperCase()}`;
  const suffix = h < 12 ? 'AM' : 'PM';
  h = h % 12 === 0 ? 12 : h % 12;
  return `${h}:${min} ${suffix}`;
};

// 'vs. Hawks' | 'Practice' | event title
export const eventLabel = (g) => {
  if (!g) return '';
  if (!g.event_type || g.event_type === 'game') return `vs. ${g.opponent}`;
  return g.title || (g.event_type === 'practice' ? 'Practice' : 'Event');
};

export const isCancelled = (g) => g?.status === 'cancelled';
export const isPostponed = (g) => g?.status === 'postponed';

// { label, tone } for the status chip; null when nothing to show.
export const statusChip = (g) => {
  if (isCancelled(g)) return { label: 'Cancelled', tone: 'red' };
  if (isPostponed(g)) return { label: 'Postponed — new date TBD', tone: 'orange' };
  return null;
};

// Outcome display for past games: { label, tone, score }
export const outcomeChip = (g) => {
  if (g.outcome === 'W') return { label: 'Win', tone: 'green', score: `${g.our_score}-${g.opponent_score}` };
  if (g.outcome === 'L') return { label: 'Loss', tone: 'red', score: `${g.our_score}-${g.opponent_score}` };
  if (g.outcome === 'T') return { label: 'Tie', tone: 'orange', score: `${g.our_score}-${g.opponent_score}` };
  if (isCancelled(g)) return { label: 'Cancelled', tone: 'gray', score: '—' };
  if (g.result) return { label: 'Needs review', tone: 'orange', score: g.result };
  return { label: 'Needs score', tone: 'orange', score: '—' };
};

// Site badge tone: Home blue, Away orange, Neutral gray.
export const siteTone = (site) => (site === 'Home' ? 'blue' : site === 'Away' ? 'orange' : 'gray');
